// In-depth JavaScript lessons. Each lesson follows the Codeazy lesson standard:
// why it matters → mental model → explanation with output → under the hood →
// common mistakes → interview questions → practice → key takeaways.

export const javascriptLessons = [
  {
    slug: "variables-and-scope",
    title: "Variables, Scope and Hoisting",
    summary: "How var, let and const really behave, and why some variables exist before their line runs.",
    readTime: 12,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Many confusing JavaScript bugs, such as a variable that is unexpectedly `undefined`, a loop that prints the same number three times, or a value that leaks into another function, come down to scope. It is also one of the first things interviewers test, because it shows whether you understand how JavaScript runs your code rather than only its syntax." },

      { type: "h2", text: "The mental model: nested boxes" },
      { type: "p", text: "Think of scopes as boxes inside boxes. Code inside a box can see variables in its own box and in every box around it, but never into a box nested inside it. When JavaScript looks up a variable, it starts in the current box and walks outwards until it finds it. This outward path is called the **scope chain**." },
      { type: "code", language: "javascript", code: `const course = "JavaScript";        // global box

function outer() {
  const level = "beginner";          // outer's box

  function inner() {
    const topic = "scope";           // inner's box
    console.log(course, level, topic);
  }

  inner();
  console.log(typeof topic);         // can't look into inner's box
}

outer();`, output: `JavaScript beginner scope
undefined` },
      { type: "p", text: "`inner` can read `course` and `level` because they are in surrounding boxes. `outer` cannot see `topic`, because it lives in a box nested inside it. This is called **lexical scoping**: what a function can see is decided by where it is written in the code, not where it is called from." },

      { type: "h2", text: "var, let and const compared" },
      { type: "table", headers: ["", "var", "let", "const"], rows: [
        ["Scope", "Function", "Block `{ }`", "Block `{ }`"],
        ["Can be reassigned", "Yes", "Yes", "No"],
        ["Can be redeclared in the same scope", "Yes", "No (SyntaxError)", "No (SyntaxError)"],
        ["Hoisted", "Yes, set to `undefined`", "Yes, but uninitialised (TDZ)", "Yes, but uninitialised (TDZ)"],
        ["Becomes a property of `window` at the top level of a script", "Yes", "No", "No"]
      ] },
      { type: "p", text: "The modern rule is simple: use `const` by default, use `let` when the value must change, and don't use `var` in new code." },
      { type: "h3", text: "Block scope in action" },
      { type: "code", language: "javascript", code: `if (true) {
  var a = "var leaks out of blocks";
  let b = "let stays inside";
}

console.log(a);
console.log(b);`, output: `var leaks out of blocks
ReferenceError: b is not defined` },
      { type: "h3", text: "const does not mean immutable" },
      { type: "p", text: "`const` stops you from pointing the variable at a different value. It does not freeze the value itself, so objects and arrays declared with `const` can still be changed." },
      { type: "code", language: "javascript", code: `const student = { name: "Asha", skills: ["HTML"] };

student.skills.push("CSS");    // allowed: changing the object
student.name = "Asha K";       // allowed
console.log(student);

student = {};                  // not allowed: reassigning the variable`, output: `{ name: 'Asha K', skills: [ 'HTML', 'CSS' ] }
TypeError: Assignment to constant variable.` },
      { type: "callout", variant: "note", text: "If you need a truly read-only object, use `Object.freeze(obj)`. It is shallow, so nested objects can still change unless you freeze them too." },

      { type: "h2", text: "Hoisting: what happens before your code runs" },
      { type: "p", text: "JavaScript runs code in two phases. In the **creation phase**, it scans the scope and sets up memory for every declaration. In the **execution phase**, it runs your code line by line. Hoisting is the effect of that first phase: declarations are known before their line runs." },
      { type: "table", headers: ["Declaration", "During the creation phase", "Using it before its line"], rows: [
        ["`var x`", "Created and set to `undefined`", "Gives `undefined`"],
        ["`let x` / `const x`", "Created but left uninitialised", "Throws `ReferenceError`"],
        ["`function f() {}`", "Created with the whole function", "Works normally"],
        ["`var f = function () {}`", "`f` is set to `undefined`", "Throws `TypeError: f is not a function`"]
      ] },
      { type: "code", language: "javascript", code: `console.log(price);    // var: hoisted as undefined
var price = 499;

sayHi();               // function declaration: fully hoisted
function sayHi() {
  console.log("Hi!");
}`, output: `undefined
Hi!` },
      { type: "h3", text: "The Temporal Dead Zone (TDZ)" },
      { type: "p", text: "Are `let` and `const` hoisted? Yes, and this example proves it:" },
      { type: "code", language: "javascript", code: `let city = "Bengaluru";

function showCity() {
  console.log(city);   // which city?
  let city = "Pune";
}

showCity();`, output: `ReferenceError: Cannot access 'city' before initialization` },
      { type: "p", text: "If the inner `let city` were not hoisted, the function would print the outer “Bengaluru”. Instead, JavaScript already knows a local `city` exists, but it is not initialised yet. The time between the start of the scope and the declaration line is the **Temporal Dead Zone**. Accessing the variable in that zone throws an error." },

      { type: "h2", text: "The classic loop question" },
      { type: "p", text: "This snippet appears in interviews constantly:" },
      { type: "code", language: "javascript", code: `for (var i = 0; i < 3; i++) {
  setTimeout(() => console.log(i), 0);
}`, output: `3
3
3` },
      { type: "p", text: "There is only one `var i` for the whole loop. The callbacks run later, after the loop has finished and `i` is 3, so all three print 3. With `let`, each iteration gets its own new binding of `i`:" },
      { type: "code", language: "javascript", code: `for (let i = 0; i < 3; i++) {
  setTimeout(() => console.log(i), 0);
}`, output: `0
1
2` },

      { type: "h2", text: "Common mistakes" },
      { type: "compare", badLabel: "Accidental global", good: `"use strict";

function calculateTotal() {
  const total = 10;
  return total;
}`, bad: `function calculateTotal() {
  total = 10; // no let/const!
  return total;
}
// total is now a global variable`, text: "Assigning to an undeclared variable silently creates a global in non-strict code. Strict mode, which ES modules and classes use automatically, turns this into an error." },
      { type: "compare", badLabel: "Shadowing by accident", good: `let count = 0;

function increment() {
  count += 1;
}`, bad: `let count = 0;

function increment() {
  let count = 0; // new variable!
  count += 1;
}`, text: "Declaring a variable with the same name inside a function creates a new one that hides the outer one, so the outer `count` never changes." },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is the difference between `var`, `let` and `const`?", answer: ["`var` is function-scoped, can be redeclared and is hoisted with the value `undefined`. `let` and `const` are block-scoped, cannot be redeclared in the same scope, and are hoisted but stay in the Temporal Dead Zone until their declaration runs. `const` also cannot be reassigned, although objects it points to can still be changed."] },
      { type: "qa", question: "Are `let` and `const` hoisted?", answer: ["Yes. They are registered during the creation phase but not initialised. Accessing them before the declaration throws a `ReferenceError` instead of returning `undefined`. That period is called the Temporal Dead Zone."] },
      { type: "qa", question: "What is lexical scoping?", answer: ["A function’s access to variables is decided by where the function is written in the source code, not where it is called. A function defined inside another function can read the outer function’s variables through the scope chain."] },
      { type: "qa", question: "What does this print, and how would you fix it?", answer: ["It prints `3 3 3`, because all callbacks share a single function-scoped `i`. Fix it by using `let`, which creates a new binding for each iteration, or by capturing the value in an IIFE (immediately invoked function expression)."], code: `for (var i = 0; i < 3; i++) {
  setTimeout(() => console.log(i), 100);
}`, language: "javascript" },

      { type: "exercise", title: "Fix the loop without using let", task: "Using only `var`, change the loop so it prints `0`, `1`, `2`. Hint: each callback needs its own copy of `i`.", language: "javascript", starter: `for (var i = 0; i < 3; i++) {
  setTimeout(() => console.log(i), 0);
}`, solution: `for (var i = 0; i < 3; i++) {
  (function (j) {
    setTimeout(() => console.log(j), 0);
  })(i);
}`, output: `0
1
2`, explanation: "The IIFE runs immediately on every iteration and receives the current value of `i` as its own parameter `j`. Each callback closes over a different `j`. This is exactly what `let` does for you automatically." },

      { type: "summary", items: [
        "Scope is lexical: a function sees variables from where it is written, walking outwards through the scope chain.",
        "Use `const` by default, `let` when you must reassign, and avoid `var`.",
        "`const` prevents reassignment, not mutation.",
        "Every declaration is hoisted, but `let` and `const` stay in the Temporal Dead Zone until their line runs.",
        "`let` in a loop creates a fresh binding per iteration, which fixes the classic `setTimeout` bug."
      ] }
    ]
  },

  {
    slug: "closures",
    title: "Closures",
    summary: "Functions that remember where they were created, and why this powers private state, React hooks and utilities like debounce.",
    readTime: 12,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Closures are behind private variables, event handlers, `debounce` and `throttle`, memoisation and every React hook. If you have written a callback that uses a variable from outside it, you have already used a closure. Interviewers love closures because they test whether you really understand scope." },

      { type: "h2", text: "The mental model: a backpack" },
      { type: "p", text: "When a function is created, it packs a **backpack** containing references to the variables around it. Wherever that function goes, returned from another function, passed as a callback or stored in an object, it carries the backpack and can still read and update those variables, even after the outer function has finished." },
      { type: "callout", variant: "note", title: "Definition", text: "A closure is a function combined with references to the variables in the scope where it was created (its lexical environment)." },

      { type: "h2", text: "Your first closure" },
      { type: "code", language: "javascript", code: `function createCounter() {
  let count = 0;               // goes into the backpack

  return function increment() {
    count += 1;
    return count;
  };
}

const counterA = createCounter();
const counterB = createCounter();

console.log(counterA()); // 1
console.log(counterA()); // 2
console.log(counterB()); // 1  ← separate backpack
console.log(count);`, output: `1
2
1
ReferenceError: count is not defined` },
      { type: "p", text: "Three things to notice:" },
      { type: "ol", items: [
        "`createCounter` has already returned, yet `increment` can still use `count`.",
        "Each call to `createCounter` creates a **new** `count`, so `counterA` and `counterB` don’t interfere with each other.",
        "Nothing outside can touch `count` directly. It is truly private."
      ] },

      { type: "h2", text: "Closures capture variables, not values" },
      { type: "p", text: "The backpack holds a live reference to the variable, not a snapshot of its value when the function was created:" },
      { type: "code", language: "javascript", code: `function makeGreeting() {
  let name = "Asha";
  const greet = () => "Hello, " + name;
  name = "Ravi";        // changed after greet was created
  return greet;
}

console.log(makeGreeting()());`, output: `Hello, Ravi` },

      { type: "h2", text: "Real-world uses" },
      { type: "h3", text: "1. Private state" },
      { type: "code", language: "javascript", code: `function createBankAccount(initial) {
  let balance = initial;

  return {
    deposit(amount) {
      if (amount <= 0) throw new Error("Amount must be positive");
      balance += amount;
      return balance;
    },
    getBalance() {
      return balance;
    }
  };
}

const account = createBankAccount(1000);
account.deposit(500);
console.log(account.getBalance());
console.log(account.balance);`, output: `1500
undefined` },
      { type: "p", text: "The only way to change `balance` is through `deposit`, which validates the amount. Closures give you encapsulation without classes." },
      { type: "h3", text: "2. Function factories" },
      { type: "code", language: "javascript", code: `function createDiscount(percent) {
  return (price) => price - (price * percent) / 100;
}

const festiveSale = createDiscount(20);
const studentOffer = createDiscount(50);

console.log(festiveSale(1000));
console.log(studentOffer(1000));`, output: `800
500` },
      { type: "h3", text: "3. once(): run a function only one time" },
      { type: "code", language: "javascript", code: `function once(fn) {
  let called = false;
  let result;
  return function (...args) {
    if (!called) {
      called = true;
      result = fn.apply(this, args);
    }
    return result;
  };
}

const initPayment = once(() => {
  console.log("Payment SDK loaded");
  return "ready";
});

initPayment();
initPayment();
console.log(initPayment());`, output: `Payment SDK loaded
ready` },
      { type: "h3", text: "4. Memoisation: caching expensive results" },
      { type: "code", language: "javascript", code: `function memoize(fn) {
  const cache = new Map();          // lives in the closure
  return function (n) {
    if (cache.has(n)) return cache.get(n);
    const result = fn(n);
    cache.set(n, result);
    return result;
  };
}

const slowSquare = (n) => {
  console.log("computing " + n);
  return n * n;
};
const fastSquare = memoize(slowSquare);

fastSquare(9);
fastSquare(9);
console.log(fastSquare(9));`, output: `computing 9
81` },

      { type: "h2", text: "Under the hood: memory" },
      { type: "p", text: "Normally a function’s local variables are garbage-collected when it returns. If an inner function that references them is still reachable, the engine keeps those variables alive. This is why closures work, and also how they can cause memory leaks: an event listener that closes over a large array keeps that array in memory until the listener is removed." },
      { type: "callout", variant: "warning", text: "Remove event listeners, intervals and subscriptions you no longer need. In React, return a cleanup function from `useEffect` for the same reason." },

      { type: "h2", text: "Stale closures in React" },
      { type: "p", text: "A stale closure is a function still reading an old value because it closed over a variable from an earlier render:" },
      { type: "compare", badLabel: "Always shows 1", bad: `function Timer() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setCount(count + 1); // count is always 0 here
    }, 1000);
    return () => clearInterval(id);
  }, []);

  return <p>{count}</p>;
}`, goodLabel: "Counts correctly", good: `function Timer() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setCount((c) => c + 1); // uses latest value
    }, 1000);
    return () => clearInterval(id);
  }, []);

  return <p>{count}</p>;
}`, text: "The interval callback was created during the first render, when `count` was 0, so it keeps calculating `0 + 1`. The functional update `setCount((c) => c + 1)` doesn’t depend on the closed-over value." },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is a closure?", answer: ["A closure is a function together with references to the variables of the scope where it was defined. It lets the function keep using those variables after the outer function has returned. Closures are used for private state, function factories, callbacks, memoisation and utilities such as `debounce`."] },
      { type: "qa", question: "How do closures help create private variables?", answer: ["Declare the variable inside a function and return only the functions that are allowed to use it. Code outside has no reference to the variable, so it can only be read or changed through the returned functions, like `deposit` and `getBalance` in the bank account example."] },
      { type: "qa", question: "Can closures cause memory leaks?", answer: ["Yes. Variables captured by a closure stay in memory as long as the closure is reachable. A long-lived listener, interval or cache that closes over large data keeps that data alive. Clean up listeners and intervals when they are no longer needed."] },
      { type: "qa", question: "What is a stale closure?", answer: ["A function that keeps using an outdated value because it captured a variable from an earlier scope or render. It is common in React with `setInterval` or effects with missing dependencies. Fix it with functional state updates, correct dependency arrays or refs."] },

      { type: "exercise", title: "Build sum(1)(2)(3)()", task: "Write a function `sum` that can be called any number of times in a row and returns the total when called with no argument. `sum(1)(2)(3)()` should return `6`, and `sum(5)()` should return `5`.", language: "javascript", starter: `function sum(a) {
  // your code here
}

console.log(sum(1)(2)(3)());
console.log(sum(5)());`, solution: `function sum(a) {
  return function (b) {
    if (b === undefined) return a;
    return sum(a + b);
  };
}

console.log(sum(1)(2)(3)());
console.log(sum(5)());`, output: `6
5`, explanation: "Each returned function closes over the running total `a`. When it receives a number, it starts a new `sum` with the updated total. When it receives nothing, it returns the total." },

      { type: "summary", items: [
        "A closure is a function plus a live reference to the variables where it was created.",
        "Closures capture variables, not values, so later changes are visible.",
        "They give you private state, factories, `once`, `memoize`, `debounce` and React hooks.",
        "Captured variables stay in memory while the closure is reachable, so clean up listeners and intervals.",
        "A stale closure reads an old value. In React, prefer functional updates and correct dependencies."
      ] }
    ]
  },

  {
    slug: "this-keyword",
    title: "The this Keyword",
    summary: "Four rules that decide what this points to, and how arrow functions change the game.",
    readTime: 11,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "`this` causes bugs like “Cannot read properties of undefined” when you pass a method as a callback, or when a class method loses its object inside a timer. Questions about `this`, `call`, `apply` and `bind` are standard in JavaScript interviews." },

      { type: "h2", text: "The mental model: who called me?" },
      { type: "p", text: "For regular functions, `this` is not decided when the function is written. It is decided **every time the function is called**, by how it is called. Look at the call site and ask “who called me?” The one exception is arrow functions, which don’t have their own `this` and take it from the surrounding code." },
      { type: "table", headers: ["How the function is called", "What this is", "Example"], rows: [
        ["With `new`", "The newly created object", "`new User(\"Asha\")`"],
        ["With `call`, `apply` or `bind`", "The object you pass in", "`greet.call(admin)`"],
        ["As a method", "The object before the dot", "`user.greet()`"],
        ["As a plain function", "`undefined` in strict mode (`globalThis` in sloppy mode)", "`greet()`"],
        ["Arrow function", "Inherited from the enclosing scope", "`() => this.name`"]
      ] },
      { type: "p", text: "The rules are listed from highest to lowest priority. If more than one could apply, the higher one wins." },

      { type: "h2", text: "Rule 1: method calls" },
      { type: "code", language: "javascript", code: `const user = {
  name: "Asha",
  greet() {
    return "Hi, I'm " + this.name;
  }
};

console.log(user.greet());   // user is before the dot`, output: `Hi, I'm Asha` },

      { type: "h2", text: "Losing this" },
      { type: "p", text: "The same function behaves differently when it is called without the object:" },
      { type: "code", language: "javascript", code: `"use strict";
const greet = user.greet;    // just a reference to the function
greet();                     // nothing before the dot`, output: `TypeError: Cannot read properties of undefined (reading 'name')` },
      { type: "p", text: "This is exactly what happens when you pass a method as a callback, such as `setTimeout(user.greet, 1000)` or `button.addEventListener(\"click\", user.greet)`. The method is called later as a plain function, so `this` is no longer `user`." },
      { type: "compare", badLabel: "this is lost", bad: `setTimeout(user.greet, 1000);`, goodLabel: "Two ways to keep it", good: `// 1. Wrap it in an arrow function
setTimeout(() => user.greet(), 1000);

// 2. Bind it permanently
setTimeout(user.greet.bind(user), 1000);` },

      { type: "h2", text: "Rule 2: call, apply and bind" },
      { type: "p", text: "These methods let you choose `this` explicitly:" },
      { type: "code", language: "javascript", code: `function introduce(city, role) {
  return this.name + " from " + city + ", " + role;
}

const mentor = { name: "Ravi" };

console.log(introduce.call(mentor, "Pune", "mentor"));     // args one by one
console.log(introduce.apply(mentor, ["Delhi", "speaker"])); // args as an array

const ravisIntro = introduce.bind(mentor, "Chennai");      // returns a new function
console.log(ravisIntro("author"));`, output: `Ravi from Pune, mentor
Ravi from Delhi, speaker
Ravi from Chennai, author` },
      { type: "table", headers: ["Method", "Calls the function now?", "How arguments are passed"], rows: [
        ["`call`", "Yes", "One by one: `fn.call(obj, a, b)`"],
        ["`apply`", "Yes", "As an array: `fn.apply(obj, [a, b])`"],
        ["`bind`", "No, it returns a new function", "Can pre-fill some: `fn.bind(obj, a)`"]
      ] },
      { type: "callout", variant: "note", text: "A bound function’s `this` cannot be changed again. Calling `.bind()` or `.call()` on it with another object has no effect on `this`." },

      { type: "h2", text: "Rule 3: arrow functions" },
      { type: "p", text: "Arrow functions don’t have their own `this`. They use the `this` of the code around them, which makes them perfect for callbacks inside methods:" },
      { type: "code", language: "javascript", code: `const stopwatch = {
  seconds: 0,
  start() {
    setInterval(() => {
      this.seconds += 1;            // this is stopwatch
      console.log(this.seconds);
    }, 1000);
  }
};

stopwatch.start();`, output: `1
2
3
...` },
      { type: "p", text: "With a regular `function () {}` callback, `setInterval` would call it as a plain function and `this.seconds` would fail." },
      { type: "compare", badLabel: "Arrow function as a method", bad: `const course = {
  title: "DSA",
  describe: () => "Course: " + this.title
};
course.describe(); // this is NOT course`, goodLabel: "Method shorthand", good: `const course = {
  title: "DSA",
  describe() {
    return "Course: " + this.title;
  }
};
course.describe(); // "Course: DSA"`, text: "Don’t use arrow functions as object methods when you need `this`. The arrow takes `this` from outside the object literal, not from the object." },

      { type: "h2", text: "Rule 4: new" },
      { type: "code", language: "javascript", code: `function Student(name) {
  this.name = name;        // this is the new object
}

const s = new Student("Meera");
console.log(s.name);`, output: `Meera` },
      { type: "p", text: "`new` creates an empty object, sets `this` to it, runs the function and returns the object. The Prototypes lesson covers this in detail." },

      { type: "h2", text: "this in classes and React" },
      { type: "p", text: "Class bodies always run in strict mode, so a method that loses its object gets `this === undefined`. This is why older React class components bound their handlers in the constructor. A class field with an arrow function avoids the problem:" },
      { type: "code", language: "javascript", code: `class Cart {
  items = [];

  // Arrow class field: this is always the instance
  add = (item) => {
    this.items.push(item);
    return this.items.length;
  };
}

const cart = new Cart();
const addToCart = cart.add;        // detached on purpose
console.log(addToCart("React course"));`, output: `1` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "How is the value of `this` determined?", answer: ["For regular functions it depends on how the function is called. With `new` it is the new object. With `call`, `apply` or `bind` it is the object passed in. As a method it is the object before the dot. As a plain call it is `undefined` in strict mode, or the global object in sloppy mode. Arrow functions don’t have their own `this` and inherit it from the enclosing scope."] },
      { type: "qa", question: "What is the difference between `call`, `apply` and `bind`?", answer: ["`call` and `apply` invoke the function immediately with a chosen `this`. `call` takes arguments one by one and `apply` takes them as an array. `bind` doesn’t invoke the function. It returns a new function with `this`, and optionally some arguments, fixed permanently."] },
      { type: "qa", question: "Why does `setTimeout(obj.method, 1000)` lose `this`?", answer: ["`obj.method` evaluates to the function itself, without any connection to `obj`. `setTimeout` later calls it as a plain function, so `this` is `undefined` in strict mode. Use `() => obj.method()` or `obj.method.bind(obj)` instead."] },
      { type: "qa", question: "What does this print?", code: `const team = {
  name: "Codeazy",
  members: ["Asha", "Ravi"],
  list() {
    return this.members.map((m) => m + " @ " + this.name);
  }
};
console.log(team.list());`, language: "javascript", output: `[ 'Asha @ Codeazy', 'Ravi @ Codeazy' ]`, answer: ["The arrow function inside `map` inherits `this` from `list`, which was called as `team.list()`, so `this.name` is “Codeazy”. With a regular function callback, `this` would be `undefined` and it would throw."] },

      { type: "exercise", title: "Write your own bind", task: "Implement `Function.prototype.myBind` so it works like the built-in `bind`. It must fix `this` and support pre-filled arguments.", language: "javascript", starter: `Function.prototype.myBind = function (context, ...boundArgs) {
  // your code here
};

function introduce(city, role) {
  return this.name + " from " + city + ", " + role;
}
const asha = { name: "Asha" };
console.log(introduce.myBind(asha, "Pune")("developer"));`, solution: `Function.prototype.myBind = function (context, ...boundArgs) {
  const fn = this; // the function myBind was called on
  return function (...args) {
    return fn.apply(context, [...boundArgs, ...args]);
  };
};

console.log(introduce.myBind(asha, "Pune")("developer"));`, output: `Asha from Pune, developer`, explanation: "Inside `myBind`, `this` is the original function, because it was called as `introduce.myBind(...)`. The returned function uses `apply` to call it with the saved context and the combined arguments." },

      { type: "summary", items: [
        "For regular functions, `this` is decided at call time by how the function is called.",
        "Priority: `new` → `call`/`apply`/`bind` → method call → plain call.",
        "Passing `obj.method` as a callback loses `this`. Wrap it in an arrow function or use `bind`.",
        "Arrow functions inherit `this` from their surroundings: great for callbacks, wrong for object methods.",
        "Classes run in strict mode, and arrow class fields keep `this` bound to the instance."
      ] }
    ]
  },

  {
    slug: "prototypes-and-classes",
    title: "Prototypes and Classes",
    summary: "How JavaScript objects inherit from each other, and what class syntax really does.",
    readTime: 12,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Ever wondered where `[1, 2, 3].map` comes from when you never wrote a `map` method? The answer is prototypes. JavaScript inheritance is built on them, and `class` is a friendlier syntax over the same mechanism. Knowing this helps you debug, read library code and answer a very common interview question." },

      { type: "h2", text: "The mental model: asking your parents" },
      { type: "p", text: "Every object has a hidden link to another object, its **prototype**. When you read a property the object doesn’t have, JavaScript asks the prototype, then the prototype’s prototype, and so on, like asking your parent and then your grandparent. This path is the **prototype chain**. It ends at `null`." },
      { type: "code", language: "javascript", code: `const animal = {
  eats: true,
  walk() {
    return "walking";
  }
};

const dog = Object.create(animal);   // dog's prototype is animal
dog.barks = true;

console.log(dog.barks);   // own property
console.log(dog.eats);    // found on animal
console.log(dog.walk());  // found on animal
console.log(Object.getPrototypeOf(dog) === animal);
console.log(Object.hasOwn(dog, "eats"));`, output: `true
true
walking
true
false` },
      { type: "p", text: "The full chain is `dog → animal → Object.prototype → null`. That is why every object has methods like `toString` and `hasOwnProperty`: they live on `Object.prototype`." },

      { type: "h2", text: "Built-ins use the same mechanism" },
      { type: "code", language: "javascript", code: `const scores = [70, 85];

console.log(Object.getPrototypeOf(scores) === Array.prototype);
console.log(Object.hasOwn(scores, "map"));
console.log(typeof Array.prototype.map);`, output: `true
false
function` },
      { type: "p", text: "Every array shares a single copy of `map`, `filter` and the rest on `Array.prototype`. Putting methods on the prototype instead of on each object saves a lot of memory." },

      { type: "h2", text: "Constructor functions and new" },
      { type: "p", text: "Before classes existed, objects were created with constructor functions. Methods were added to the function’s `prototype` property so all instances could share them:" },
      { type: "code", language: "javascript", code: `function Person(name) {
  this.name = name;                       // own property per instance
}

Person.prototype.greet = function () {    // shared by all instances
  return "Hi, I am " + this.name;
};

const asha = new Person("Asha");
console.log(asha.greet());
console.log(Object.getPrototypeOf(asha) === Person.prototype);`, output: `Hi, I am Asha
true` },
      { type: "h3", text: "What new actually does" },
      { type: "ol", items: [
        "Creates a new empty object.",
        "Links its prototype to `Person.prototype`.",
        "Runs `Person` with `this` set to the new object.",
        "Returns the new object, unless the function explicitly returns a different object."
      ] },

      { type: "h2", text: "prototype vs __proto__" },
      { type: "table", headers: ["Term", "What it is", "Exists on"], rows: [
        ["`Person.prototype`", "The object that will become the prototype of instances created with `new Person()`", "Functions and classes"],
        ["`obj.__proto__`", "A legacy getter for an object’s actual prototype", "Every object"],
        ["`Object.getPrototypeOf(obj)`", "The modern, recommended way to read an object’s prototype", "Every object"]
      ] },
      { type: "p", text: "So `asha.__proto__ === Person.prototype`. Use `Object.getPrototypeOf` and `Object.create` in real code." },

      { type: "h2", text: "Classes: the same thing with nicer syntax" },
      { type: "code", language: "javascript", code: `class Person {
  constructor(name) {
    this.name = name;
  }

  greet() {
    return "Hi, I am " + this.name;
  }
}

class Developer extends Person {
  constructor(name, stack) {
    super(name);               // must run before using this
    this.stack = stack;
  }

  greet() {                    // override
    return super.greet() + " and I build with " + this.stack;
  }
}

const ravi = new Developer("Ravi", "React");
console.log(ravi.greet());
console.log(ravi instanceof Person);
console.log(typeof Person);`, output: `Hi, I am Ravi and I build with React
true
function` },
      { type: "p", text: "`typeof Person` is `\"function\"`. A class is still a constructor function, and `greet` is still stored on `Person.prototype`. `extends` sets up the chain `Developer.prototype → Person.prototype`." },

      { type: "h3", text: "Modern class features" },
      { type: "code", language: "javascript", code: `class BankAccount {
  static bankName = "Codeazy Bank";   // on the class, not instances
  #balance = 0;                        // truly private field

  deposit(amount) {
    if (amount <= 0) throw new Error("Amount must be positive");
    this.#balance += amount;
  }

  get balance() {                      // read like a property
    return this.#balance;
  }
}

const acc = new BankAccount();
acc.deposit(2000);
console.log(acc.balance);
console.log(BankAccount.bankName);

// acc.#balance → SyntaxError: Private field '#balance'
// must be declared in an enclosing class`, output: `2000
Codeazy Bank` },

      { type: "h2", text: "Common mistakes" },
      { type: "compare", badLabel: "Method copied into every instance", bad: `function Person(name) {
  this.name = name;
  this.greet = function () {
    return "Hi, " + this.name;
  };
}`, goodLabel: "One shared method", good: `class Person {
  constructor(name) {
    this.name = name;
  }
  greet() {
    return "Hi, " + this.name;
  }
}`, text: "Defining methods inside the constructor creates a new function for every instance. Prototype or class methods are shared." },
      { type: "compare", badLabel: "this before super()", bad: `class Admin extends Person {
  constructor(name) {
    this.role = "admin"; // ReferenceError
    super(name);
  }
}`, goodLabel: "super() first", good: `class Admin extends Person {
  constructor(name) {
    super(name);
    this.role = "admin";
  }
}` },
      { type: "callout", variant: "warning", text: "Don’t add methods to built-in prototypes such as `Array.prototype.myHelper = ...` in application code. It can clash with future JavaScript features and with libraries." },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is the prototype chain?", answer: ["Every object has an internal link to another object, its prototype. When a property isn’t found on an object, JavaScript looks it up on the prototype, then on that object’s prototype, and so on until it reaches `null`. This is how inheritance and shared methods work in JavaScript."] },
      { type: "qa", question: "What is the difference between `prototype` and `__proto__`?", answer: ["`prototype` is a property of constructor functions and classes. It is the object that instances created with `new` will inherit from. `__proto__` is a legacy accessor on every object that points to its actual prototype. For an instance, `instance.__proto__ === Constructor.prototype`."] },
      { type: "qa", question: "Are classes in JavaScript real classes like in Java?", answer: ["No. They are syntactic sugar over constructor functions and prototypes. A class is a function, its methods live on its `prototype`, and `extends` links prototypes. Classes do add useful rules, though: they must be called with `new`, they always run in strict mode, and they support private `#fields`."] },
      { type: "qa", question: "How does `instanceof` work?", answer: ["`obj instanceof C` walks up the prototype chain of `obj` and returns `true` if it finds `C.prototype` anywhere on it."] },

      { type: "exercise", title: "Implement new yourself", task: "Write `myNew(Constructor, ...args)` that behaves like the `new` keyword. Follow the four steps listed above.", language: "javascript", starter: `function myNew(Constructor, ...args) {
  // your code here
}

function Person(name) { this.name = name; }
Person.prototype.greet = function () { return "Hi, I am " + this.name; };

const meera = myNew(Person, "Meera");
console.log(meera.greet());
console.log(meera instanceof Person);`, solution: `function myNew(Constructor, ...args) {
  // Steps 1 and 2: new object linked to the prototype
  const obj = Object.create(Constructor.prototype);
  // Step 3: run the constructor with this = obj
  const result = Constructor.apply(obj, args);
  // Step 4: return the object, unless the constructor returned its own object
  const isObject = result !== null && (typeof result === "object" || typeof result === "function");
  return isObject ? result : obj;
}

console.log(meera.greet());
console.log(meera instanceof Person);`, output: `Hi, I am Meera
true`, explanation: "`Object.create` handles steps 1 and 2 in one call. The final check matches the real `new`: if a constructor explicitly returns an object, that object is used instead." },

      { type: "summary", items: [
        "Every object links to a prototype, and property lookups walk the prototype chain until `null`.",
        "Shared methods live on the prototype, which is why every array has `map` without copying it.",
        "`new` creates an object, links its prototype, runs the constructor and returns the object.",
        "`class` and `extends` are syntax over prototypes, and a class is still a function.",
        "Use `Object.getPrototypeOf` and `Object.create` rather than `__proto__`."
      ] }
    ]
  },

  {
    slug: "event-loop",
    title: "The Event Loop",
    summary: "How single-threaded JavaScript runs timers, promises and clicks, and how to predict output order.",
    readTime: 13,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Why does `setTimeout(fn, 0)` not run immediately? Why does a heavy loop freeze the page? In what order will these logs print? All three are answered by the event loop. “Predict the output” questions about it appear in most front-end and Node.js interviews." },

      { type: "h2", text: "The mental model: a restaurant with one chef" },
      { type: "p", text: "JavaScript has a single main thread, like a restaurant with **one chef**. The chef cooks one dish at a time: that is the **call stack**. Waiters handle anything that involves waiting, such as timers, network requests and clicks: those are the **browser APIs**. When something is ready, a waiter puts a ticket in a queue. The chef only picks up a new ticket when the current dish is completely done, and always clears the **VIP queue** (microtasks) before taking a regular ticket (macrotasks)." },
      { type: "table", headers: ["Part", "What it does", "Examples"], rows: [
        ["Call stack", "Runs JavaScript, one function at a time", "Your synchronous code"],
        ["Browser / Node APIs", "Do the waiting outside the main thread", "Timers, `fetch`, DOM events, file I/O"],
        ["Microtask queue", "High-priority callbacks, all run as soon as the stack is empty", "`.then` / `.catch` / `.finally`, code after `await`, `queueMicrotask`"],
        ["Macrotask (task) queue", "Regular callbacks, one per loop turn", "`setTimeout`, `setInterval`, events, I/O"]
      ] },

      { type: "h2", text: "The algorithm" },
      { type: "ol", items: [
        "Run one macrotask to completion. Your initial script is the first one.",
        "Run **every** microtask in the queue, including any microtasks added while doing so.",
        "Let the browser render if needed.",
        "Go back to step 1."
      ] },
      { type: "callout", variant: "note", text: "This is why promise callbacks always run before `setTimeout` callbacks that were queued at the same time: microtasks are drained completely before the next macrotask." },

      { type: "h2", text: "Walkthrough: predict the output" },
      { type: "code", language: "javascript", code: `console.log("A");

setTimeout(() => console.log("B"), 0);

Promise.resolve().then(() => console.log("C"));

queueMicrotask(() => console.log("D"));

console.log("E");`, output: `A
E
C
D
B` },
      { type: "table", headers: ["Step", "What happens", "Microtasks", "Macrotasks"], rows: [
        ["1", "Log **A**", "", ""],
        ["2", "`setTimeout` hands the timer to the browser, which queues B", "", "B"],
        ["3", "`.then` queues C", "C", "B"],
        ["4", "`queueMicrotask` queues D", "C, D", "B"],
        ["5", "Log **E**. The script (the first macrotask) is done", "C, D", "B"],
        ["6", "Drain microtasks: log **C**, then **D**", "", "B"],
        ["7", "Next macrotask: log **B**", "", ""]
      ] },

      { type: "h2", text: "await is a microtask too" },
      { type: "p", text: "Code before the first `await` in an async function runs synchronously. Everything after it is queued as a microtask:" },
      { type: "code", language: "javascript", code: `async function run() {
  console.log("1");
  await null;
  console.log("2");     // queued as a microtask
}

console.log("3");
run();
console.log("4");`, output: `3
1
4
2` },

      { type: "h2", text: "Microtasks run between every macrotask" },
      { type: "code", language: "javascript", code: `setTimeout(() => {
  console.log("timer 1");
  Promise.resolve().then(() => console.log("promise inside timer 1"));
}, 0);

setTimeout(() => console.log("timer 2"), 0);`, output: `timer 1
promise inside timer 1
timer 2` },
      { type: "p", text: "After `timer 1` finishes, the event loop drains the microtask queue before taking `timer 2`." },

      { type: "h2", text: "Blocking the main thread" },
      { type: "p", text: "Because there is only one chef, a long synchronous task blocks everything else: clicks, rendering and timers. A `setTimeout` delay is a **minimum**, not a guarantee:" },
      { type: "code", language: "javascript", code: `const start = Date.now();

setTimeout(() => {
  console.log("ran after", Date.now() - start, "ms");
}, 100);

// Busy loop for 500ms: blocks the stack
while (Date.now() - start < 500) {}`, output: `ran after 500 ms   (roughly, never ~100)` },
      { type: "p", text: "The timer finished after 100ms, but its callback had to wait in the queue until the stack was free." },
      { type: "h3", text: "How to keep the UI responsive" },
      { type: "ul", items: [
        "Split heavy work into chunks and yield between them with `setTimeout` so the browser can render and respond.",
        "Move CPU-heavy work, such as parsing large files or image processing, to a **Web Worker**, which runs on another thread.",
        "Use `requestAnimationFrame` for visual updates. It runs right before the browser paints."
      ] },
      { type: "callout", variant: "warning", text: "Microtasks can block too. A promise chain that keeps queueing new microtasks will never let the event loop reach rendering or timers. This is called starvation." },

      { type: "h2", text: "A note on Node.js" },
      { type: "p", text: "Node.js has the same core idea with a few extra queues. `process.nextTick` callbacks run before promise microtasks, and `setImmediate` runs in a phase after I/O callbacks. For interviews, the browser model above, plus “nextTick before promises”, covers most questions." },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "Is JavaScript single-threaded? Then how does it handle async work?", answer: ["JavaScript runs on a single main thread with one call stack. Waiting work such as timers, network and file I/O is handled by the browser or Node.js runtime, often on other threads. When that work completes, its callback is queued, and the event loop moves queued callbacks onto the stack whenever it is empty."] },
      { type: "qa", question: "What is the difference between microtasks and macrotasks?", answer: ["Microtasks are promise callbacks, code after `await` and `queueMicrotask`. Macrotasks include `setTimeout`, `setInterval`, I/O and UI events. After each macrotask, the event loop runs all queued microtasks before the next macrotask or render. So microtasks always run first."] },
      { type: "qa", question: "Why doesn’t `setTimeout(fn, 0)` run immediately?", answer: ["It queues `fn` as a macrotask after a minimum delay of about 0ms. It can only run after the current synchronous code finishes and all pending microtasks have run. Browsers may also enforce a minimum delay of around 4ms for deeply nested timers."] },

      { type: "exercise", title: "Predict the output", task: "Write down the exact order of the logs before revealing the answer. Use the algorithm: synchronous code first, then all microtasks in order, then macrotasks.", language: "javascript", starter: `console.log("start");

setTimeout(() => console.log("timeout"), 0);

Promise.resolve()
  .then(() => console.log("promise 1"))
  .then(() => console.log("promise 2"));

(async () => {
  console.log("async start");
  await undefined;
  console.log("async end");
})();

console.log("end");`, solution: `// Synchronous:  start, async start, end
// Microtasks:   promise 1, async end, promise 2
// Macrotasks:   timeout`, output: `start
async start
end
promise 1
async end
promise 2
timeout`, explanation: "After the synchronous code, the microtask queue holds [promise 1, async end]. Running `promise 1` queues `promise 2` at the end, giving [async end, promise 2]. Only after all microtasks finish does the `setTimeout` callback run." },

      { type: "summary", items: [
        "One call stack runs your code, and runtime APIs do the waiting.",
        "After each macrotask, the event loop drains all microtasks, then may render.",
        "Promise callbacks and code after `await` are microtasks, so they beat `setTimeout`.",
        "A `setTimeout` delay is a minimum, and long synchronous code delays everything.",
        "Keep the UI responsive by chunking work or moving it to Web Workers."
      ] }
    ]
  },

  {
    slug: "promises-async-await",
    title: "Promises and async/await",
    summary: "Handle asynchronous work cleanly: chaining, error handling, parallel requests and common traps.",
    readTime: 13,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Every API call, database query and file read in modern JavaScript is asynchronous. Writing async code well, with correct error handling and requests running in parallel where possible, is the difference between an app that feels fast and one that hangs or fails silently." },

      { type: "h2", text: "The mental model: an order token" },
      { type: "p", text: "At a busy food counter, you order and receive a **token**. You don’t have the food yet, but the token promises an outcome: either your food arrives (**fulfilled**) or the counter tells you they are out of stock (**rejected**). Until then it is **pending**. You decide in advance what to do in each case. A promise is that token." },
      { type: "table", headers: ["State", "Meaning", "Handled with"], rows: [
        ["Pending", "The work is still in progress", "-"],
        ["Fulfilled", "Completed with a value", "`.then(value => ...)` or `await`"],
        ["Rejected", "Failed with a reason, usually an `Error`", "`.catch(err => ...)` or `try/catch`"]
      ] },
      { type: "p", text: "Once a promise is fulfilled or rejected, it is **settled** and never changes state again." },

      { type: "h2", text: "Creating a promise" },
      { type: "code", language: "javascript", code: `function fetchCourse(id) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (id <= 0) {
        reject(new Error("Invalid course id"));
      } else {
        resolve({ id, title: "System Design" });
      }
    }, 300);
  });
}

fetchCourse(1)
  .then((course) => console.log("Got:", course.title))
  .catch((err) => console.log("Failed:", err.message));

fetchCourse(-1)
  .then((course) => console.log("Got:", course.title))
  .catch((err) => console.log("Failed:", err.message));`, output: `Got: System Design
Failed: Invalid course id` },
      { type: "callout", variant: "tip", text: "Most of the time you won’t write `new Promise` yourself, because `fetch`, database drivers and most libraries already return promises. You mainly need it to wrap old callback-based APIs." },

      { type: "h2", text: "Chaining" },
      { type: "p", text: "`.then` always returns a **new** promise. What you return inside it decides what the next step receives:" },
      { type: "ul", items: [
        "Return a value: the next `.then` gets that value.",
        "Return a promise: the chain waits for it, then passes on its result.",
        "Throw an error: the chain skips to the next `.catch`."
      ] },
      { type: "code", language: "javascript", code: `fetchCourse(1)
  .then((course) => course.title.toUpperCase())   // returns a value
  .then((title) => {
    if (title.length > 5) throw new Error("Title too long");
    return title;
  })
  .then((title) => console.log("Never reached:", title))
  .catch((err) => console.log("Caught:", err.message))
  .finally(() => console.log("Done loading"));`, output: `Caught: Title too long
Done loading` },

      { type: "h2", text: "async/await: promises that read like normal code" },
      { type: "p", text: "An `async` function always returns a promise. Inside it, `await` pauses that function, not the whole program, until the promise settles, then gives you the value or throws the error." },
      { type: "compare", badLabel: "Callback pyramid", bad: `getUser(id, (err, user) => {
  if (err) return handle(err);
  getOrders(user.id, (err, orders) => {
    if (err) return handle(err);
    getInvoice(orders[0].id, (err, invoice) => {
      if (err) return handle(err);
      render(invoice);
    });
  });
});`, goodLabel: "async/await", good: `async function showLatestInvoice(id) {
  try {
    const user = await getUser(id);
    const orders = await getOrders(user.id);
    const invoice = await getInvoice(orders[0].id);
    render(invoice);
  } catch (err) {
    handle(err);
  }
}` },
      { type: "h3", text: "A real fetch with proper error handling" },
      { type: "code", language: "javascript", code: `async function loadCourses() {
  setLoading(true);
  try {
    const res = await fetch("/api/courses");
    if (!res.ok) {                       // fetch does NOT reject on 404/500
      throw new Error("Server responded " + res.status);
    }
    return await res.json();
  } catch (err) {
    showToast("Could not load courses: " + err.message);
    return [];
  } finally {
    setLoading(false);                   // runs on success and failure
  }
}` },
      { type: "callout", variant: "warning", text: "`fetch` only rejects on network failures. A `404` or `500` response still fulfils, so always check `res.ok`." },

      { type: "h2", text: "Sequential vs parallel" },
      { type: "p", text: "`await` in a row runs requests one after another. If they don’t depend on each other, start them together:" },
      { type: "compare", badLabel: "Sequential: about 2 seconds", bad: `// Each call takes about 1s
const profile = await getProfile();
const courses = await getCourses();`, goodLabel: "Parallel: about 1 second", good: `const [profile, courses] = await Promise.all([
  getProfile(),
  getCourses()
]);` },

      { type: "h2", text: "Promise combinators" },
      { type: "table", headers: ["Method", "Resolves when", "Rejects when", "Use it for"], rows: [
        ["`Promise.all`", "All fulfil (array of values)", "Any one rejects (fails fast)", "Requests that all must succeed"],
        ["`Promise.allSettled`", "All settle (array of `{status, value/reason}`)", "Never", "Batch jobs where you want every result"],
        ["`Promise.race`", "The first one settles, whichever way", "The first one rejects", "Timeouts"],
        ["`Promise.any`", "The first one fulfils", "All reject (`AggregateError`)", "Fastest of several mirrors or servers"]
      ] },
      { type: "code", language: "javascript", code: `const results = await Promise.allSettled([
  Promise.resolve("profile loaded"),
  Promise.reject(new Error("orders service down"))
]);

results.forEach((r) => console.log(r.status, r.value ?? r.reason.message));`, output: `fulfilled profile loaded
rejected orders service down` },
      { type: "h3", text: "Adding a timeout with Promise.race" },
      { type: "code", language: "javascript", code: `function withTimeout(promise, ms) {
  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error("Timed out after " + ms + "ms")), ms)
  );
  return Promise.race([promise, timeout]);
}

await withTimeout(fetch("/api/slow"), 3000);` },

      { type: "h2", text: "Common mistakes" },
      { type: "compare", badLabel: "await inside forEach", bad: `async function saveAll(ids) {
  ids.forEach(async (id) => {
    await save(id);
  });
  console.log("All saved!"); // logs immediately
}`, goodLabel: "for...of or Promise.all", good: `async function saveAll(ids) {
  // one at a time:
  for (const id of ids) {
    await save(id);
  }
  // or all at once:
  // await Promise.all(ids.map(save));
  console.log("All saved!");
}`, text: "`forEach` ignores the promises returned by an async callback, so it never waits for them." },
      { type: "compare", badLabel: "Forgetting to return", bad: `getUser(1)
  .then((user) => {
    getOrders(user.id); // not returned!
  })
  .then((orders) => console.log(orders));
// undefined`, goodLabel: "Return the promise", good: `getUser(1)
  .then((user) => getOrders(user.id))
  .then((orders) => console.log(orders));` },
      { type: "compare", badLabel: "Wrapping a promise in a promise", bad: `function getData() {
  return new Promise((resolve, reject) => {
    fetch("/api").then(resolve).catch(reject);
  });
}`, goodLabel: "Just return it", good: `function getData() {
  return fetch("/api");
}`, text: "Wrapping an existing promise in `new Promise` adds code and makes it easy to lose errors." },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What are the states of a promise?", answer: ["Pending, fulfilled and rejected. A promise starts pending and becomes settled (fulfilled with a value or rejected with a reason) exactly once. After that its state never changes."] },
      { type: "qa", question: "What is the difference between `Promise.all` and `Promise.allSettled`?", answer: ["`Promise.all` rejects as soon as any input rejects and gives you only the values if all succeed. `Promise.allSettled` always waits for every input and returns an array of `{ status, value }` or `{ status, reason }` objects, so you can handle successes and failures individually."] },
      { type: "qa", question: "What does an async function return?", answer: ["Always a promise. A returned value becomes the fulfilled value, and a thrown error becomes the rejection reason. Even `async () => 5` returns `Promise<5>`."] },
      { type: "qa", question: "How do you run async calls in parallel and still handle errors?", answer: ["Start them together and await `Promise.all` inside `try/catch`. If you need every result even when some fail, use `Promise.allSettled` and check each `status`."] },

      { type: "exercise", title: "Implement Promise.all", task: "Write `promiseAll(items)` that behaves like `Promise.all`. It should resolve with values in the **original order**, reject as soon as any input rejects, and accept plain values as well as promises.", language: "javascript", starter: `function promiseAll(items) {
  // your code here
}

const wait = (ms, value) => new Promise((r) => setTimeout(() => r(value), ms));

promiseAll([wait(300, "slow"), 42, wait(100, "fast")]).then(console.log);`, solution: `function promiseAll(items) {
  return new Promise((resolve, reject) => {
    const results = new Array(items.length);
    let completed = 0;

    if (items.length === 0) return resolve(results);

    items.forEach((item, index) => {
      Promise.resolve(item)            // turns plain values into promises
        .then((value) => {
          results[index] = value;      // keep the original order
          completed += 1;
          if (completed === items.length) resolve(results);
        })
        .catch(reject);                // first rejection wins
    });
  });
}`, output: `[ 'slow', 42, 'fast' ]`, explanation: "Storing each value at its `index` keeps the order even though “fast” finishes first. The counter tells you when everything is done, and calling `reject` on the first error gives the fail-fast behaviour." },

      { type: "summary", items: [
        "A promise is pending, then settles once as fulfilled or rejected.",
        "`.then` returns a new promise: return values to pass them on, and throw to jump to `.catch`.",
        "`async` functions always return promises. Use `try/catch/finally` with `await`.",
        "`fetch` doesn’t reject on HTTP errors, so check `res.ok`.",
        "Run independent work in parallel with `Promise.all`, and never `await` inside `forEach`."
      ] }
    ]
  }
];
